import { useRef, useState, type CSSProperties, type ReactElement } from "react";
import { MessageCircleQuestion, Plus } from "lucide-react";
import { Button } from "@primitives/Button";
import { IconButton } from "@primitives/IconButton";
import { Menu, MenuItem } from "@primitives/overlay";
import { ACTION_SHORTCUTS } from "./actionShortcuts";

/**
 * CreateActions: Ask and New, the two ways to start work.
 *
 *  - `labeled`  two ghost buttons with icon + label.
 *  - otherwise  one `+` that opens a menu with Ask and New.
 *
 * Lives in the top bar. Tooltips name the shortcut.
 */

export interface CreateActionsProps {
  readonly onAsk: () => void;
  readonly onCreate: () => void;
  readonly labeled: boolean;
  readonly testId?: string;
}

const ASK_TOOLTIP = `Ask a question (${ACTION_SHORTCUTS.ask})`;
const CREATE_TOOLTIP = `New workflow (${ACTION_SHORTCUTS.create})`;

const clusterStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
};

export function CreateActions({
  onAsk,
  onCreate,
  labeled,
  testId,
}: CreateActionsProps): ReactElement {
  if (!labeled) {
    return (
      <CompactCreateActions
        onAsk={onAsk}
        onCreate={onCreate}
        {...(testId !== undefined ? { testId } : {})}
      />
    );
  }

  return (
    <div style={clusterStyle} data-testid={testId}>
      <Button
        variant="ghost"
        size="sm"
        onClick={onAsk}
        title={ASK_TOOLTIP}
        aria-keyshortcuts={ACTION_SHORTCUTS.ask}
      >
        <MessageCircleQuestion size={16} strokeWidth={2.25} aria-hidden="true" />
        Ask
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onCreate}
        title={CREATE_TOOLTIP}
        aria-keyshortcuts={ACTION_SHORTCUTS.create}
      >
        <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
        New
      </Button>
    </div>
  );
}

function CompactCreateActions({
  onAsk,
  onCreate,
  testId,
}: Omit<CreateActionsProps, "labeled">): ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);

  return (
    <div style={clusterStyle} data-testid={testId}>
      <IconButton
        ref={menuTriggerRef}
        icon={Plus}
        aria-label="Ask or start"
        tooltip="Ask a question or start a workflow"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((prev) => !prev)}
      />
      <Menu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchorRef={menuTriggerRef}
        placement="bottom-end"
        ariaLabel="Ask or start"
        sheetOnCompact={false}
        testId="create-actions-menu"
      >
        <MenuItem
          icon={<MessageCircleQuestion size={16} strokeWidth={2.25} aria-hidden="true" />}
          label="Ask"
          description="Send a question to a queue"
          onSelect={onAsk}
        />
        <MenuItem
          icon={<Plus size={16} strokeWidth={2.25} aria-hidden="true" />}
          label="New"
          description="Start a workflow"
          onSelect={onCreate}
        />
      </Menu>
    </div>
  );
}
